import { WithAuthorization } from "../../context";
import { useTranslation } from "react-i18next";
import { HeatmapCard } from "./components";
import { Card } from "../../components/Card";
import { Page } from "../../components/Page";
import { Button, chakra, Divider, Flex, Heading, Text, useToast } from "@chakra-ui/react";
import { FormSelect } from "../../components/Forms";
import { calculateDefaultValues, generateArrayOfYears, showErrorToast } from "../../utils";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import {
  useExportSuppliersPhysicalRiskData,
  usePhysicalRiskReport
} from "../../hooks/query/physical-risk/physical-risk";
import { usePhysicalRiskGreggsReport } from "../../hooks/query/physical-risk/physical-risk-greggs";
import {
  colors,
  colorsForGreggs,
  colorsForMenzies,
  depotsHeaders,
  menziesHeaders,
  ownAssetsHeaders,
  storesRiskHeaders,
  suppliersRiskHeaders,
  supplyRiskHeaders
} from "./helpers";
import { usePhysicalRiskMenziesReport } from "../../hooks/query/physical-risk/physical-risk-menzies";
import React, {lazy, Suspense} from "react";

// SupplierRiskData is lazy loaded because it's a large component (the only user of the big mapbox dependency)
const SupplierRiskData = lazy(() => import("./components/SupplierRiskData"));

function PhysicalRisk() {
  const { t } = useTranslation("translation", {
    keyPrefix: "physicalRisk"
  });
  const dispatch = useDispatch();
  const toast = useToast();

  const {
    register,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: calculateDefaultValues()
  });

  const handleErrors = (err) => {
    if (err.response?.status === 404 || err.response?.status === 403) {
      return;
    }
    showErrorToast(toast, err.message);
  };

  const watchYear = watch("financialYear");
  const watchQuarter = watch("quarter");

  const queryConfig = {
    onError: err => handleErrors(err),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
    refetchOnWindowFocus: false
  };

  const {
    data: physicalRiskData,
    isFetching,
    error: physicalRiskError
  } = usePhysicalRiskReport({
    year: watchYear,
    quarter: watchQuarter
  }, queryConfig);

  const {
    data: physicalRiskGreggsData,
    isFetchingGreggs,
    error: physicalRiskErrorGreggs
  } = usePhysicalRiskGreggsReport({
    year: watchYear,
    quarter: watchQuarter
  }, queryConfig);

  const {
    data: physicalRiskMenziesData,
    isFetchingMenzies,
    error: physicalRiskErrorMenzies
  } = usePhysicalRiskMenziesReport({
    year: watchYear,
    quarter: watchQuarter
  }, queryConfig);

  const isClimateRiskNormalEmpty = physicalRiskError && physicalRiskError.response && physicalRiskError.response.status === 404;
  const isClimateRiskGreggsEmpty = physicalRiskErrorGreggs && physicalRiskErrorGreggs.response && physicalRiskErrorGreggs.response.status === 404;
  const isClimateRiskMenziesEmpty = physicalRiskErrorMenzies && physicalRiskErrorMenzies.response && physicalRiskErrorMenzies.response.status === 404;

  const {
    mutate: exportSuppliersPhysicalRiskData
  } = useExportSuppliersPhysicalRiskData({
    onError: err => {
      const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
      showErrorToast(toast, parsedErrorObj?.message ?? err.message);
      dispatch(hideAppSpinner());
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const handleExportClick = () => {
    dispatch(showAppSpinner());
    exportSuppliersPhysicalRiskData({ params: { year: watchYear, quarter: watchQuarter } });
  };

  const subdomain = window.location.hostname.split(".")[0];
  const subdomainList = ["localhost", "qa", "staging", "demo", "pkf", "pwc"];

  return (
    <Page title={t("page.title")} subTitle={t("page.subTitle")}>
      <Card>
        <Heading size="md" marginBlock="1">
          {t("selectPeriod")}
        </Heading>
        <chakra.form id="calculateReport">
          <Flex gap="4" marginBlockStart="5" alignItems="end">
            <FormSelect
              label={t("inputs.financialYear")}
              errors={errors}
              {...register("financialYear", { required: true })}
            >
              {generateArrayOfYears().map((year) => <option key={year} value={year}>{year}</option>)}
            </FormSelect>
            <FormSelect
              label={t("inputs.quarter")}
              errors={errors}
              {...register("quarter", { required: true })}
            >
              <option key="ALL" value="ALL">ALL</option>
              <option key="Q1" value="Q1">Q1</option>
              <option key="Q2" value="Q2">Q2</option>
              <option key="Q3" value="Q3">Q3</option>
              <option key="Q4" value="Q4">Q4</option>
            </FormSelect>
          </Flex>
        </chakra.form>
      </Card>

      <Divider marginBlock="5" />

      <Card>
        <Flex alignItems="center" justifyContent="space-between">
          <Heading size="md" marginBlock="1">
            {t("suppliersPhysicalRiskQuestionnaires")}
          </Heading>
          <Button
            colorScheme="customScheme"
            type="submit"
            title={t("inputs.exportBtnTitle")}
            width="32"
            onClick={handleExportClick}
          >
            {t("inputs.export")}
          </Button>
        </Flex>
      </Card>

      {!subdomainList.includes(subdomain) && !isClimateRiskNormalEmpty && !isFetching && physicalRiskData &&
        <Card>
          {/* Suppliers Risk Heatmap */}
          <HeatmapCard
            title={t("suppliersSection.title")}
            containerId={"suppliersRiskContainer"}
            data={physicalRiskData.supplierRiskItems}
            headers={suppliersRiskHeaders}
            colors={colors}
            tooltipAttributes={["supplierName"]}
            clickHandling={{
              clickableAttribute: "supplierName",
              availableDataAttribute: "supplierData"
            }}
          />
          {/* Own Assets Risk Heatmap */}
          <HeatmapCard
            title={t("ownAssetsSection.title")}
            containerId={"ownAssetsContainer"}
            data={physicalRiskData.ownAssetRiskItems}
            headers={ownAssetsHeaders}
            colors={colors}
            tooltipAttributes={["location"]}
          />
          {/* Depots Risk Heatmap */}
          <HeatmapCard
            title={t("depotsSection.title")}
            containerId={"depotsContainer"}
            data={physicalRiskData.depotRiskItems}
            headers={depotsHeaders}
            colors={colors}
            tooltipAttributes={["depot"]}
          />
        </Card>
      }

      {!subdomainList.includes(subdomain) && !isClimateRiskGreggsEmpty && !isFetchingGreggs && physicalRiskGreggsData &&
        <Card>
          {/* Greggs Supply Risk Heatmap */}
          <HeatmapCard
            title={t("supplyRiskSection.title")}
            containerId={"supplyRiskContainer"}
            data={physicalRiskGreggsData.supplyRiskItems}
            headers={supplyRiskHeaders}
            colors={colorsForGreggs}
            tooltipAttributes={["location"]}
          />
          {/* Greggs Store Risk Heatmap */}
          <HeatmapCard
            title={t("storesRiskSection.title")}
            containerId={"storeRiskContainer"}
            data={physicalRiskGreggsData.storeRiskItems}
            headers={storesRiskHeaders}
            colors={colorsForGreggs}
            tooltipAttributes={["location"]}
          />
        </Card>
      }

      {!subdomainList.includes(subdomain) && !isClimateRiskMenziesEmpty && !isFetchingMenzies && physicalRiskMenziesData &&
        <Card>
          {/* Menzies Climate Risk Heatmap */}
          <HeatmapCard
            title={t("menziesSection.title")}
            containerId={"menziesContainer"}
            data={physicalRiskMenziesData.menziesClimateItem}
            headers={menziesHeaders}
            colors={colorsForMenzies}
            tooltipAttributes={["location"]}
          />
        </Card>
      }

      {!subdomainList.includes(subdomain) && isClimateRiskNormalEmpty && isClimateRiskGreggsEmpty && isClimateRiskMenziesEmpty &&
        <Card>
          <Text fontSize="lg" fontWeight="bold" marginTop={5}>
            {t("missingDataError")}
          </Text>
        </Card>
      }

      <Suspense fallback={<div>Loading...</div>}>
        {subdomainList.includes(subdomain) && <SupplierRiskData subdomain={subdomain} />}
      </Suspense>
    </Page>
  );
}

export default WithAuthorization(PhysicalRisk);